import React from 'react';
import classNames from 'classnames';

import BusComponent from '../BusComponent';
import MapViewer from '../MapViewer';
import ApplicationFrameType from '../../enums/ApplicationFrameType';
import AppConfig from '../../appConfig';
import MapFiltersIndicator from '../mapIndicators/MapFiltersIndicator';
import MapLogo from '../mapControls/MapLogo';
import MapControls from '../mapControls/MapControls';
import MapFocusButton from '../mapControls/MapFocusButton';
import { resizeCanvas } from '../../helpers/Util';
import { HelpTourTargets } from '../../enums/HelpTourDefinitions';
import DataBrowserWrapper from '../dataBrowser/DataBrowserWrapper';
import CondensedFrameComponents from './CondensedFrameComponents';
import MapInfoBubbleSwitch from '../mapControls/MapInfoBubbleSwitch';
import ReportIframe from '../locationPanel/ReportIframe';
import PointsPanel from '../pointsPanel/PointsPanel';
import Mixpanel, { MIXPANEL_EVENTS } from '../../helpers/Mixpanel';
import MapLayersPanelContainer from '../mapLayersPanel/MapLayersPanelContainer';
import { getMapDefaults, saveMapDefaults } from '../../helpers/mapDefaults';

class SingleMapFrame extends BusComponent {
    constructor(props, context) {
        super(props, context);

        this.state = {
            reportIframeVisible: !!this.props.frame.mapInstances[0].locationAnalysisItem,
            isLayersPanelVisible: getMapDefaults().legendOpen === true,
            userClosedLayers: false,
            isPanelCollapsed: false,
            isLegendCollapsed: false,
            visualReports: [],
            dealLists: [],
        };
    }

    componentWillMount() {
        this.emit('APPLICATION_FRAME_TYPE_CHANGED', ApplicationFrameType.SINGLE_MAP);
        AppConfig.sentryRecordEvent('entered single map frame');
        if (this.props.displayDataBrowser) {
            this.handleDataBrowserRequest();
        }
    }

    componentDidMount() {
        this.bus.emit('DEAL_LISTS_GET_REQUEST');
        this.bus.emit('VISUAL_REPORTS_GET_REQUEST', { source: this });
        this.bindGluBusEvents({
            LOCATION_ANALYSIS_MODE_STARTED: this.handleShowReportIframe,
            LOCATION_ANALYSIS_MODE_EXITED: this.handleCloseReportIframe,
            ENTER_UPDATE_LOCATION_ANALYSIS_MODE: this.exitFocus,
            COLLAPSE_ALL: this.handleCollapseAll,
            TOGGLE_MAP_LAYERS_PANEL: this.handleToggleLayersPanel,
            DEAL_LISTS: this.onDealLists,
            VISUAL_REPORTS: this.onVisualReports,
        });
    }

    componentWillUnmount() {
        this.unbindGluBusEvents();
    }

    onDealLists = ({ dealLists }) => {
        this.setState({ dealLists });
    };

    onVisualReports = ({ visualReports }) => {
        this.setState({ visualReports }, () => {
            // Force a re-render of child components that depend on visualReports
        });
    };

    handleShowReportIframe = () => {
        this.exitFocus();
        this.setState({ reportIframeVisible: true });
    };

    handleCloseReportIframe = () => {
        this.exitFocus();
        this.setState({ reportIframeVisible: false });
    };

    isFocused = () => this.state.isPanelCollapsed || this.state.isLegendCollapsed;

    exitFocus = () => {
        if (!this.isFocused()) return;
        this.setState(({ isLayersPanelVisible, isLegendCollapsed }) => ({
            isPanelCollapsed: false,
            isLegendCollapsed: false,
            isLayersPanelVisible: isLayersPanelVisible || isLegendCollapsed,
        }));
    };

    handleToggleFocus = () => {
        if (this.isFocused()) {
            this.exitFocus();
            return;
        }
        this.setState(({ isLayersPanelVisible }) => ({
            isPanelCollapsed: true,
            isLegendCollapsed: isLayersPanelVisible,
            isLayersPanelVisible: false,
        }));
    };

    handleExpandPanel = () => {
        this.setState({ isPanelCollapsed: false });
    };

    handleMapImageReady = imageData => {
        const canvas = document.createElement('canvas');
        canvas.width = imageData.width;
        canvas.height = imageData.height;

        const ctx = canvas.getContext('2d');
        ctx.putImageData(imageData, 0, 0);
        ctx.globalCompositeOperation = 'copy';
        ctx.scale(1, -1);
        ctx.translate(0, -canvas.height);
        ctx.drawImage(canvas, 0, 0);

        const thumbnail = resizeCanvas(canvas).toDataURL();
        const thumbnailLarge = canvas.toDataURL('image/jpeg', 0.7);

        this.emit('UPDATE_CURRENT_FRAME_THUMBNAIL_REQUEST', {
            thumbnail,
            thumbnailLarge,
        });
    };

    handleDataBrowserRequest = () => {
        Mixpanel.emitUserEvent(MIXPANEL_EVENTS.CHANGE_DATA);
        this.setState({ showDataBrowser: true });
    };

    handleDataBrowserClose = () => {
        this.setState({ showDataBrowser: false });
    };

    handleCollapseAll = () => {
        this.setState({ showDataBrowser: false });

        // Defer to allow the data browser to close before hiding the layers panel
        setTimeout(() => {
            this.bus.emit('TOGGLE_MAP_LAYERS_PANEL', { isVisible: false });
        }, 0);
    };

    handleToggleLayersPanel = ({ isVisible, source = 'system', reason }) => {
        // Always allow Help Tour to override
        if (reason === 'HELP_TOUR') {
            this.setState({ isLayersPanelVisible: isVisible, userClosedLayers: false });
            return;
        }

        if (source === 'system') return;

        saveMapDefaults(this.bus, { legendOpen: isVisible });

        this.setState({
            isLayersPanelVisible: isVisible,
            isLegendCollapsed: false,
        });
    };

    renderFrameComponents() {
        const { frame, isUserStarterTier, isCondensedLayout, children, currentUser } = this.props;

        const { showDataBrowser, visualReports, reportIframeVisible, isPanelCollapsed } = this.state;
        const { applicationMode } = this.context;
        const [mapInstance] = frame.mapInstances;

        if (isCondensedLayout) {
            return (
                <CondensedFrameComponents
                    mapViewer={this.map}
                    isUserStarterTier={isUserStarterTier}
                    mapInstances={frame.mapInstances}
                    activeMapIndex={0}
                    onShowDataBrowser={this.handleDataBrowserRequest}
                    applicationMode={applicationMode}
                    isLayersPanelVisible={this.state.isLayersPanelVisible}
                >
                    {children}
                </CondensedFrameComponents>
            );
        }

        const isPanelExpanded = reportIframeVisible && !isPanelCollapsed;

        const componentBottomRightClasses = classNames('frame__elements__bottom-right', {
            'frame__elements__bottom-right--with-margin': isPanelExpanded,
            'frame__elements__bottom-right--with-bar': isPanelCollapsed,
        });

        const componentTopRightClasses = classNames('frame__elements__top-right', {
            'frame__elements__top-right--with-margin': isPanelExpanded,
            'frame__elements__top-right--with-bar': isPanelCollapsed,
        });

        return (
            <div className="frame__elements">
                {AppConfig.constants.hasFilterInSearchPanel && (
                    <PointsPanel mapInstance={mapInstance} />
                )}
                {this.map && !showDataBrowser && (
                    <MapLayersPanelContainer
                        isVisible={this.state.isLayersPanelVisible}
                        mapInstance={mapInstance}
                        mapInstanceIndex={frame.mapInstances.indexOf(mapInstance)}
                        currentUser={currentUser}
                        onShowDataBrowser={this.handleDataBrowserRequest}
                        mapViewer={this.map}
                        tourId={HelpTourTargets.MAP_LAYERS_PANEL}
                        visualReports={visualReports}
                    />
                )}
                <div className={componentTopRightClasses}>
                    <div className="flex-it flex-end stretch-width">
                        <MapFiltersIndicator mapInstance={mapInstance} />
                    </div>
                </div>
                <div className={componentBottomRightClasses}>
                    <div className="flex-it column center">
                        {this.props.children}
                        {reportIframeVisible && (
                            <MapFocusButton
                                isFocused={this.isFocused()}
                                onClick={this.handleToggleFocus}
                            />
                        )}
                        <MapControls mapInstance={mapInstance} />
                    </div>
                </div>
                <MapInfoBubbleSwitch mapInstance={mapInstance} />
                {AppConfig.constants.showMapFooterLogo && <MapLogo />}
            </div>
        );
    }

    render() {
        const { frame, isCondensedLayout, currentUser } = this.props;
        const { dealLists, visualReports, isPanelCollapsed } = this.state;

        const [mapInstance] = frame.mapInstances;

        const frameClasses = classNames('frame frame--single', {
            'frame--condensed-layout': isCondensedLayout,
        });

        return (
            <div className={frameClasses}>
                <div className="frame__map">
                    <MapViewer
                        ref={c => (this.map = c)}
                        mapInstance={mapInstance}
                        onImageReady={this.handleMapImageReady}
                        annotations
                    />
                </div>
                {this.renderFrameComponents()}
                {this.state.showDataBrowser && (
                    <DataBrowserWrapper
                        mapInstance={mapInstance}
                        onClose={this.handleDataBrowserClose}
                    />
                )}
                <ReportIframe
                    mapInstance={mapInstance}
                    currentUser={currentUser}
                    dealLists={dealLists}
                    visualReports={visualReports}
                    collapsed={isPanelCollapsed}
                    onExpand={this.handleExpandPanel}
                />
            </div>
        );
    }
}

export default SingleMapFrame;
